import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import PracticeSkillsContainer from './PracticeSkillsContainer';
import SkillPopOverContent from './SkillPopOverContent';
import uniqid from 'uniqid';
import isFn from 'lodash/isFunction';
import {
  getSortedSkillsWithParentCategory,
  getSortedSkillsFromCategory,
} from './utils';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

import './PracticeSkillsPopOver.scss';

const PracticeSkillsPopOver = ({
  widgetTagColumn,
  widgetTagColumnLabel,
  metadata,
  flatMetadata = [],
  skillsList = [],
  className,
  skillsContainerClassName,
  skills = null,
  columns = null,
}) => {
  const [count, setCount] = useState(-1);
  const skillsContainerRef = useRef();
  const skillsContainerRef2 = useRef();
  let sortedSkills;
  if (skills) {
    sortedSkills = skills;
  } else if (flatMetadata.length > 0) {
    sortedSkills = getSortedSkillsFromCategory(
      widgetTagColumn,
      widgetTagColumnLabel,
      flatMetadata,
    );
  } else if (!metadata) {
    sortedSkills = skillsList;
  } else {
    sortedSkills = getSortedSkillsWithParentCategory(
      widgetTagColumn,
      widgetTagColumnLabel,
      metadata,
    );
  }

  const skillLength = sortedSkills.length;
  const showNav = skillLength > 1;
  const isNextButtonDisabled = count >= skillLength - 1;
  const isPrevButtonDisabled = count <= 0;

  const skill = count < 0 ? sortedSkills[0] : sortedSkills[count];

  const onOpen = () => {
    // set the count to 0 when the user clicks between skills chips
    if (count < 0) {
      setCount(0);
    }
    if (
      skillsContainerRef2.current &&
      isFn(skillsContainerRef2.current.focus)
    ) {
      skillsContainerRef2.current.focus();
    }
  };

  const onCloseHandler = () => {
    setCount(-1);
    if (skillsContainerRef.current && isFn(skillsContainerRef.current.focus)) {
      skillsContainerRef.current.focus();
    }
  };

  const updateCount = (updatedCount) => {
    setCount(updatedCount);
    if (
      skillsContainerRef2.current &&
      isFn(skillsContainerRef2.current.focus)
    ) {
      skillsContainerRef2.current.focus();
    }
  };

  useEffect(() => {
    if (
      document.querySelector('body.chameleon-body') === document.activeElement
    ) {
      document.querySelector('.popover__content')?.focus();
    }
  }, [count]);

  const ids = new Array(sortedSkills.length).fill(null).map(() => uniqid());

  const onPrevClick = (event) => {
    event.stopPropagation();
    updateCount(count - 1);
  };

  const onNextClick = (event) => {
    event.stopPropagation();
    updateCount(count + 1);
  };

  return (
    <PopOver
      tabIndex={-1}
      withPortal
      showCloseButton
      className={cn('PracticeSkillsPopOver', className)}
      onOpen={onOpen}
      onClose={onCloseHandler} // reset the count so we can remove the focus
      closeOnBackwardsFocus
      trigger={
        <PracticeSkillsContainer
          skills={sortedSkills}
          activeSkillIndex={count}
          onSetSkillCount={setCount}
          ariaDescribedBy={ids}
          setCount={setCount}
          ref={skillsContainerRef}
          columns={columns}
          className={skillsContainerClassName}
        />
      }
      accessibility={{
        'aria-expanded': null,
      }}
    >
      <div className="content-wrapper">
        {showNav && (
          <div className="self-center">
            <ButtonWithIcon
              isOutline
              isIcon
              onClick={onPrevClick}
              aria-label="previous"
              aria-hidden={isPrevButtonDisabled}
              disabled={isPrevButtonDisabled}
              aria-controls={ids[count]}
              icon="navigate_back"
              iconAccessibility={{
                ariaHidden: true,
              }}
            />
          </div>
        )}

        <SkillPopOverContent
          skill={skill}
          id={ids[count]}
          ref={skillsContainerRef2}
        />

        {showNav && (
          <div className="self-center -ml-8">
            <ButtonWithIcon
              isOutline
              isIcon
              onClick={onNextClick}
              aria-label="next"
              aria-hidden={isNextButtonDisabled}
              disabled={isNextButtonDisabled}
              aria-controls={ids[count]}
              icon="navigate_next"
              iconAccessibility={{
                ariaHidden: true,
              }}
            />
          </div>
        )}
      </div>
    </PopOver>
  );
};

export default PracticeSkillsPopOver;
